'use client';

import React, { useState, useEffect } from "react";
import { Box, Typography, Card, CardActionArea, CardContent, IconButton, Slide, Fade, Grid } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { useTelegram } from "@/providers/TelegramProvider";
import TopUpView from "../views/TopUpView";
import { useLogoControl } from "@/providers/AppProvider";
import DevicesView from "../views/DevicesView";
import InstallView from "../views/InstallView";
import SupportView from "../views/SupportView";
import {Terms} from "./Terms";
import TransactionsView from "../views/TransactionsView";
import TroubleshootView from "../views/TroubleshootView";
import AdminView from "../views/AdminView";
import ReferralsView from "../views/ReferralsView";
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { isPromoActive } from "@/utils/promo";

type View = 'dashboard' | 'install' | 'devices' | 'support' | 'top-up' | 'terms' | 'transactions' | 'troubleshoot' | 'admin' | 'referrals';

const ADMIN_TG_ID = 877956556;

const Dashboard: React.FC = () => {
	const { user } = useTelegram();
	const balance = user?.wallet ?? 0;
	const [devicesCount, setDevicesCount] = useState(0);
    const [referralsCount, setReferralsCount] = useState(0);
	const theme = useTheme();

	const [view, setView] = useState<View>('dashboard');
	const { setLogoVisible } = useLogoControl();
    const [promoActive, setPromoActive] = useState(false);

	React.useEffect(() => {
		if (view === 'install' || view === 'troubleshoot' || view === 'admin') {
			setLogoVisible(false);
		} else {
			setLogoVisible(true);
		}
	}, [view, setLogoVisible]);

    useEffect(() => {
        // Мы вызываем обновление promoActive каждый раз, когда меняется user.
        // Если user загрузился (или обновился), проверяем акцию с учетом его даты регистрации.
        const userCreatedAt = user?.created ? new Date(user.created) : undefined;
        setPromoActive(isPromoActive(userCreatedAt));
    }, [user]);

    // Управление нативной кнопкой назад через window.Telegram.WebApp
    useEffect(() => {
        const tg = (window as any).Telegram?.WebApp;
        if (!tg) return;

        tg.ready();

        const handleBack = () => {
            setView('dashboard');
        };

        if (view !== 'dashboard') {
            tg.BackButton.show();
            tg.BackButton.onClick(handleBack);
        } else {
            tg.BackButton.hide();
        }

        return () => {
            tg.BackButton.offClick(handleBack);
        };
    }, [view]);

    // Загружаем количество устройств и рефералов только если мы на дашборде
    React.useEffect(() => {
        const fetchData = async () => {
            try {
                // Загрузка устройств
                const devicesResponse = await fetch('/devices');
                const devicesData = await devicesResponse.json();
                if (devicesData.success && devicesData.devices) {
                    setDevicesCount(devicesData.devices.length);
                }

                // Загрузка рефералов
                const referralsResponse = await fetch('/referrals');
                const referralsData = await referralsResponse.json();
                if (referralsData.success && referralsData.referrals) {
                    setReferralsCount(referralsData.referrals.length);
                }
            } catch (e) {
                // Silent error
            }
        };
        
        if (user && view === 'dashboard') {
            fetchData();
        }
    }, [user, view]);

	const handleNavigate = (newView: View) => setView(newView);
	const handleBack = () => setView('dashboard');

	// Style for glass effect tiles
	const glassCardStyle = {
		backgroundColor: "rgba(255, 255, 255, 0.07)",
		backdropFilter: "blur(15px)",
		WebkitBackdropFilter: "blur(15px)", // For Safari support
		boxShadow: "0 8px 32px 0 rgba(0, 0, 0, 0.15)",
		textDecoration: 'none',
		width: '100%',
        height: '100%', // Для одинаковой высоты в Grid
	};

	const renderDashboard = () => (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                width: "100%",
            }}
        >
            <Card sx={glassCardStyle}>
                <CardContent
                    sx={{
                        p: 3,
                        "&:last-child": { pb: 3 },
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        minHeight: "100px",
                    }}
                >
                    <Box>
                        <Box>
                            <Typography
                                variant="h4"
                                component="div"
                                color={
                                    "text.primary"
                                }
                                sx={{ fontWeight: "bold", mb: 1 }}
                            >
                                {balance}
                                <Typography
                                    variant="body1"
                                    component={"span"}
                                    fontWeight={"bold"}
                                    sx={{
                                        color: theme.palette.gray[300],
                                        pl: 1,
                                    }}
                                >
                                    ₽
                                </Typography>
                            </Typography>
                        </Box>
                        <Box>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ fontSize: "12px" }}
                            >
                                Тариф 5₽/день за устройство
                            </Typography>
                            {balance < 15 && (
                                <Typography
                                    variant="body2"
                                    sx={{
                                        mt: 0.5,
                                        fontSize: "12px",
                                        color: theme.palette.action.contrast,
                                        fontWeight: "bold",
                                    }}
                                >
                                    Пополните баланс
                                </Typography>
                            )}
                        </Box>
                    </Box>

                    <Box sx={{ position: 'relative' }}>
                        {promoActive && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: -10,
                                    right: 60, // Сдвинули влево
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    animation: 'bounce 2s infinite',
                                    '@keyframes bounce': {
                                        '0%, 100%': { transform: 'translateX(0)' },
                                        '50%': { transform: 'translateX(-5px)' },
                                    }
                                }}
                            >
                                <Typography 
                                    variant="h6" // Увеличили шрифт
                                    sx={{ 
                                        color: 'error.main', 
                                        fontWeight: '900',
                                        textShadow: '0 0 10px rgba(220, 38, 38, 0.5)',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    +30%
                                </Typography>
                                {/* Изогнутая стрелка SVG */}
                                <svg width="40" height="20" viewBox="0 0 40 20" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ transform: 'rotate(10deg) translateY(5px)' }}>
                                    <path d="M2 10C10 10 20 10 35 10M35 10L28 3M35 10L28 17" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </Box>
                        )}
                        <IconButton
                            onClick={() => handleNavigate('top-up')}
                            size="large"
                            sx={{
                                p: 1,
                                backgroundColor: theme.palette.orange[600],
                                border: promoActive ? `2px solid ${theme.palette.error.main}` : 'none',
                                boxShadow: promoActive ? `0 0 15px ${theme.palette.error.main}` : 'none', // Добавил свечение
                                ":hover": {
                                    backgroundColor: theme.palette.primary.main,
                                },
                            }}
                        >
                            <AddIcon
                                sx={{
                                    borderRadius: "50%",
                                    fontSize: 34,
                                    color: theme.palette.text.primary,
                                }}
                            />
                        </IconButton>
                    </Box>
                </CardContent>
            </Card>

            <Card sx={{ bgcolor: theme.palette.orange[600], border: 0, width: '100%' }}>
                <CardActionArea onClick={() => handleNavigate('install')}>
                    <CardContent
                        sx={{
                            p: 3,
                            "&:last-child": { pb: 3 },
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems:'center'
                        }}
                    >
                        <Box>
                            <Typography variant="h6" sx={{ fontSize: "16px", color: theme.palette.primary.contrastText, fontWeight: "bold", mb: 1 }}>
                                Установка и подключение
                            </Typography>
                            <Typography variant="body2" sx={{ color: theme.palette.orange[200], fontSize: "12px", fontWeight: "medium" }}>
								Инструкция по шагам
                            </Typography>
                        </Box>
                        <Box>
                            <KeyboardArrowRightIcon sx={{ fontSize: 50, color: theme.palette.text.primary, backgroundColor:theme.palette.grey[900], borderRadius: "50%", p: 1}}/>
                        </Box>
                    </CardContent>
                </CardActionArea>
            </Card>

            {/* Сетка для Устройств и Рефералов */}
            <Grid container spacing={1}>
                <Grid size={6}>
                    <Card sx={glassCardStyle}>
                        <CardActionArea onClick={() => handleNavigate('devices')} sx={{ height: '100%' }}>
                            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                                <Typography variant="h6" sx={{ fontWeight: "medium", mb: 0.5, fontSize: "12px", color: theme.palette.gray[400] }}>
                                    Мои устройства
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: "medium" }}>
                                    {devicesCount}
                                </Typography>
                            </CardContent>
                        </CardActionArea>
                    </Card>
                </Grid>
                <Grid size={6}>
                    <Card sx={glassCardStyle}>
                        <CardActionArea onClick={() => handleNavigate('referrals')} sx={{ height: '100%' }}>
                            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                                <Typography variant="h6" sx={{ fontWeight: "medium", mb: 0.5, fontSize: "12px", color: theme.palette.gray[400] }}>
                                    Рефералы
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: "medium" }}>
                                    {referralsCount}
                                </Typography>
                            </CardContent>
                        </CardActionArea>
                    </Card>
                </Grid>
            </Grid>
            
            <Card sx={glassCardStyle}>
                <CardActionArea onClick={() => handleNavigate('support')}>
                    <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
						<Typography variant="h6" sx={{ fontWeight: "medium", mb: 1, fontSize: "12px", color: theme.palette.gray[400] }}>
							Поддержка
						</Typography>
						<Typography variant="h6" sx={{ fontWeight: "bold", fontSize: "14px" }}>
							Помощь, история, документация
						</Typography>
                    </CardContent>
                </CardActionArea>
            </Card>

            {/* Кнопка админки (только для админа) */}
            {user?.telegram_id === ADMIN_TG_ID && (
                <Card sx={glassCardStyle}>
                    <CardActionArea onClick={() => handleNavigate('admin')}>
                        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 }, display: 'flex', alignItems: 'center', gap: 2 }}>
                            <AdminPanelSettingsIcon color="primary" />
                            <Typography variant="h6" sx={{ fontWeight: "bold", fontSize: "14px" }}>
                                Админ панель
                            </Typography>
                        </CardContent>
                    </CardActionArea>
                </Card>
            )}
        </Box>
	);

	const renderView = () => {
        const content = (() => {
            switch (view) {
                case 'install':
                    return <InstallView onBack={handleBack} />;
                case 'devices':
                    return <DevicesView onBack={handleBack} />;
                case 'referrals':
                    return <ReferralsView onBack={handleBack} />;
                case 'support':
                    return <SupportView onBack={handleBack} onNavigate={(v) => handleNavigate(v as View)} />;
                case 'top-up':
                    return <TopUpView onBack={handleBack} />;
                case 'terms':
                    return <Terms onClose={handleBack}/>;
                case 'transactions':
                    return <TransactionsView onBack={handleBack} />;
                case 'troubleshoot':
                    return <TroubleshootView onBack={handleBack} />;
                case 'admin':
                    return <AdminView onBack={handleBack} />;
                case 'dashboard':
                default:
                    return renderDashboard();
            }
        })();

        // Для полноэкранных view используем Fade
        if (view === 'install' || view === 'troubleshoot' || view === 'admin') {
            return (
                <Fade in={true} timeout={300} key={view}>
                    <Box sx={{ width: '100%', height: '100%' }}>
                        {content}
                    </Box>
                </Fade>
            );
        }

        // Если мы на дашборде - значит вернулись назад (анимация слева направо)
        if (view === 'dashboard') {
            return (
                <Slide in={true} direction="right" timeout={300} key="dashboard">
                    <Box sx={{ width: '100%', height: '100%' }}>
                        {content}
                    </Box>
                </Slide>
            );
        }

        // Если переходим на другой экран - анимация снизу вверх
        return (
            <Slide in={true} direction="up" timeout={300} key={view}>
                <Box sx={{ width: '100%', height: '100%' }}>
                    {content}
                </Box>
            </Slide>
        );
	};

	return (
		<>
			{renderView()}
		</>
	);
};

export { Dashboard };